import { useEffect, useRef, useState } from 'react'
import { getRun } from './api'
import type { SparkyRunStatus } from './types'

const POLL_MS = 2000

export interface SparkyRunState {
  status: SparkyRunStatus | null
  currentStep: string | null
  error: string | null
  loading: boolean
}

const IDLE: SparkyRunState = {
  status: null,
  currentStep: null,
  error: null,
  loading: false,
}

const TERMINAL: ReadonlySet<SparkyRunStatus> = new Set<SparkyRunStatus>(['completed', 'failed'])

/**
 * Polls `GET /sparky/.../runs/{id}` every ~2s until the run reaches a terminal
 * status (`completed`/`failed`), then stops — a near-clone of the journey's
 * `useJourneyRun`, against the sparky run endpoint (spec §8). Cleans up its
 * timer and ignores late responses on unmount via a `cancelled` flag; pass
 * `null` (no active run) to idle.
 *
 * `onTerminal(status)` fires exactly once per run so the sheet can reload the
 * messages (the assistant placeholder fills on completion). The callback is
 * held in a ref so a fresh closure each render never re-triggers the effect.
 */
export function useSparkyRun(
  conversationId: string | null,
  runId: string | null,
  onTerminal?: (status: SparkyRunStatus) => void,
): SparkyRunState {
  const active = conversationId !== null && runId !== null
  const [state, setState] = useState<SparkyRunState>(active ? { ...IDLE, loading: true } : IDLE)

  // Reset synchronously during render when the tracked run changes (React's
  // "store previous prop in state" pattern), so the effect only ever calls
  // setState from its async callbacks.
  const [trackedId, setTrackedId] = useState(runId)
  if (trackedId !== runId) {
    setTrackedId(runId)
    setState(active ? { ...IDLE, loading: true } : IDLE)
  }

  const onTerminalRef = useRef(onTerminal)
  useEffect(() => {
    onTerminalRef.current = onTerminal
  }, [onTerminal])

  useEffect(() => {
    if (conversationId === null || runId === null) return

    let cancelled = false
    let timer: number | undefined
    let fired = false

    async function poll() {
      try {
        const data = await getRun(conversationId as string, runId as string)
        if (cancelled) return
        setState({
          status: data.status,
          currentStep: data.current_step,
          error: data.error_message,
          loading: false,
        })
        if (TERMINAL.has(data.status)) {
          if (!fired) {
            fired = true
            onTerminalRef.current?.(data.status)
          }
        } else {
          timer = window.setTimeout(poll, POLL_MS)
        }
      } catch {
        if (cancelled) return
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'Kon de status niet ophalen.',
        }))
        timer = window.setTimeout(poll, POLL_MS)
      }
    }

    void poll()
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [conversationId, runId])

  return state
}
